import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Button,
    Box,
    FormControl,
    FormLabel,
    Input,
    Textarea,
    RadioGroup,
    Stack,
    Radio,
    Text,
    SimpleGrid,
    Flex,
    Image,
    Tooltip,
    FormErrorMessage,
    Checkbox,
    Switch
} from '@chakra-ui/react'
import { useForm, FormProvider, FieldValues, Control } from 'react-hook-form'
import ControlledSelect from '../account/select-multi'
import { Category, useCategoriesQuery } from '@/src/queries/upload/categories-query'
import { Language, useLanguagesQuery } from '@/src/queries/auth/languages-query'
import { toast } from 'sonner'
import {
    useEditVideoDetailsMutation,
    type EditVideoDetails,
} from '@/src/mutations/upload/save-video-mutation'
import { FileUploader } from 'react-drag-drop-files'
import { Info, Plus, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useVideoDetailsQuery } from '@/src/queries/video/video-detail'
import { is } from 'date-fns/locale'
import { validateImageResolution } from '@/src/lib/utils'
import { countUnicodeCharacters } from '@/src/lib/text-length'
import { format } from 'util';

interface EditVideoModalProps {
    isOpen: boolean
    onClose: () => void
    videoData: any
}
const inputStyles = {
    size: {
        base: 'md',
    },
    rounded: 'md',
    border: '1px solid #E5E5E5',
    px: {
        base: '3',
        md: '4',
    },
    fontSize: {
        base: 'sm',
        md: 'md',
    },
    mt: 1.5,
}
const labelStyles = {
    fontSize: {
        base: 'sm',
        md: '15px',
    },
    mt: {
        base: '1',
        md: '1',
    },
    fontWeight: '500',
    marginBottom: '5px',
    color: '#797979',
    opacity: 1,
}

const sanitizeVideoTitle = (value?: string) => {
    return (value ?? '').replace(/[^A-Za-z0-9|,.\-()&'"\/?:\s\u0080-\uFFFF]/g, '')
}

const getLengthValidationMessage = (
    value: string | undefined,
    min: number,
    max: number,
    fieldName: string
) => {
    const characterCount = countUnicodeCharacters(value)

    if (characterCount < min) {
        return `${fieldName} must be at least ${min} characters.`
    }

    if (characterCount > max) {
        return `${fieldName} cannot exceed ${max} characters.`
    }

    return true
}

export default function EditVideoDetailsModal({
    isOpen,
    onClose,
    videoData,
}: EditVideoModalProps) {
    const categories = useCategoriesQuery().data
    const languages = useLanguagesQuery().data
    const updateVideo = useEditVideoDetailsMutation()
    const {
        data: videoDetails,
        isPending: isLoading,
        error,
        isError,
    } = useVideoDetailsQuery(String(videoData.encryptVideoUrl).replace('/l/', ''), isOpen)
    const [thumbnailError, setThumbnailError] = useState(false)
    const thumbnailErrorRef = useRef<HTMLDivElement>(null)
    const [uploadedThumbURL, setUploadedThumbURL] = useState(videoData.thumbnailImage)
    const [uploadedThumbnail, setUploadedThumbnail] = useState('')
    const [count, setCount] = useState({ title: 0, description: 0 })

    const [uploadedThumbnailFile, setUploadedThumbnailFile] =
        useState<File | null>(null)
    const [selectedThumbnail, setSelectedThumbnail] = useState(-1)
    const setThumbnail = (index: number) => {
        setSelectedThumbnail(index)
        if (index >= 0) {
            setThumbnailError(false)
        }
    }
    const [previouslyPublished, setPreviouslyPublished] = useState(!!videoDetails?.original_publish_date)
    const fileTypes = ['JPG', 'PNG', 'JPEG']
    const findCategoriesByIds = (ids: string, categories?: Category[]) => {
        if (!ids) return undefined
        if (!categories) return undefined
        const categoryIds = ids.split(',')
        const category = categories.find((cat) => cat.categoryId === categoryIds[0])
        if (category) {
            return { label: category.categoryName, value: category.categoryId, }
        }
        return undefined
    }
    const findLanguagesByIds = (ids: string, languages?: Language[]) => {
        if (!ids) return undefined
        if (!languages) return undefined
        const languageIds = ids.split(',')
        const language = languages.find((lang) => lang.id === languageIds[0])
        if (language) {
            return { label: language.language, value: language.id }
        }
        return undefined
    }
    const videoCategory = findCategoriesByIds(videoDetails?.categoryIds || '', categories)
    const videoLanguage = findLanguagesByIds(videoDetails?.languageIds || '', languages)
    const methods = useForm({
        defaultValues: {
            title: videoDetails?.eventTitle,
            description: videoDetails?.eventContent,
            category: videoCategory,
            language: videoLanguage,
            isMadeForKids: videoDetails?.isMadeForKids ? '1' : '0',
            isRestrictAge: videoDetails?.isRestrictAge === '1' ? true : false,
            id: videoDetails?.eventId,
            original_publish_date: videoDetails?.original_publish_date || '',
        },
        values: {
            title: videoDetails?.eventTitle,
            description: videoDetails?.eventContent,
            category: videoCategory,
            language: videoLanguage,
            isMadeForKids: videoDetails?.isMadeForKids ? '1' : '0',
            isRestrictAge: videoDetails?.isRestrictAge === '1' ? true : false,
            id: videoDetails?.eventId,
            original_publish_date: videoDetails?.original_publish_date || '',
        },
    })
    const {
        register,
        handleSubmit,
        control,
        formState: { errors },
        watch,
        resetField,
    } = methods
    const titleField = register('title', {
        required: 'Title is required',
        validate: {
            characterLength: (value) =>
                getLengthValidationMessage(value, 60, 80, 'Title'),
            allowedCharacters: (value) =>
                sanitizeVideoTitle(value) === value ||
                'Only letters, numbers, spaces, |, commas, hyphens, periods, parentheses, ampersands, apostrophes, quotes, slashes, question marks, colons, and emojis are allowed in the title.',
        },
    })
    const descriptionField = register('description', {
        required: 'Description is required',
        validate: (value) =>
            getLengthValidationMessage(value, 120, 5000, 'Description'),
    })

    const onSubmit = (values: any) => {
        if (!uploadedThumbnail && !uploadedThumbURL) {
            setThumbnailError(true)
            thumbnailErrorRef.current?.scrollIntoView({ behavior: 'smooth' })
            return
        }
        const data: EditVideoDetails = {
            title: values.title,
            description: values.description,
            isMadeForKids: values.isMadeForKids,
            eventId: Number(values.id),
            thumbnailUrl: uploadedThumbnailFile ? uploadedThumbnailFile : uploadedThumbURL,
            uploadThumbnail: uploadedThumbnailFile ? true : false,
            languageId: [Number(values.language.value)],
            categoryId: [Number(values.category.value)],
            isRestrictAge: values.isRestrictAge ? '1' : '0',
            original_publish_date: previouslyPublished ? values.original_publish_date || '' : '',
        }
        updateVideo.mutate(data, {
            onSuccess: () => {
                toast.success('Changes saved successfully')
                onClose()
            },
            onError: (err: any) => toast.error(err.message),
        })
    }
    function getBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.readAsDataURL(file)
            reader.onload = () => {
                resolve(reader.result as string)
            }
            reader.onerror = (error) => {
                reject('Error: ' + error)
            }
        })
    }

    useEffect(() => {
        if (videoDetails?.thumbnailImage) {
            setUploadedThumbURL(videoDetails.thumbnailImage)
            setCount({
                title: countUnicodeCharacters(videoDetails.eventTitle),
                description: countUnicodeCharacters(videoDetails.eventContent)
            })
        }
        if (videoDetails?.original_publish_date) {
            setPreviouslyPublished(true)
        }
    }, [videoDetails?.thumbnailImage, videoDetails?.original_publish_date])

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            closeOnOverlayClick={false}
            autoFocus={false}
            returnFocusOnClose={false}
            size={['full', 'full', 'xl']}
            isCentered
            scrollBehavior="inside"
        >
            <ModalOverlay overflowX={'hidden'} />
            <ModalContent
                overflowX={'hidden'}
                rounded="xl"
                maxW={[null, null, '800px']}
                h={['100%', '90%', '90%']}
                maxH={'620px'}
            >
                <ModalHeader>
                    Edit Video Details
                </ModalHeader>
                <ModalCloseButton mt={2} />
                <hr />

                <FormProvider {...methods}>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <ModalBody pb={6}>

                            <FormControl isRequired isInvalid={!!errors.title}>
                                <FormLabel {...labelStyles}>Title</FormLabel>
                                <Tooltip
                                    label="Title must be between 60 and 80 characters."
                                    hasArrow
                                    openDelay={300}
                                >
                                    <Input
                                        {...inputStyles}
                                        key={videoDetails?.eventTitle}
                                        placeholder="Enter title here"
                                        type="text"
                                        {...titleField}
                                        onChange={(e) => {
                                            const sanitizedValue = sanitizeVideoTitle(e.target.value)
                                            e.target.value = sanitizedValue
                                            titleField.onChange(e)
                                            setCount({ ...count, title: countUnicodeCharacters(sanitizedValue) })
                                        }}
                                    />
                                </Tooltip>
                                <span style={{ color: count.title > 80 ? "#ed837f" : "#3E3E3E", fontSize: 14, textAlign: 'right', display: 'block' }}>{count.title}/80</span>
                                <FormErrorMessage style={{ marginTop: "-18px" }}>
                                    <>{errors.title && errors.title.message}</>
                                </FormErrorMessage>
                            </FormControl>
                            <FormControl

                                isRequired
                                isInvalid={!!errors.description}
                            >
                                <FormLabel {...labelStyles}>Description</FormLabel>
                                <Tooltip
                                    label="Description must be between 120 and 5000 characters."
                                    hasArrow
                                    openDelay={300}
                                >
                                    <Textarea
                                        key={videoDetails?.eventContent}
                                        placeholder="Enter video description"
                                        minH={40}
                                        maxH={80}
                                        {...inputStyles}
                                        {...descriptionField}
                                        onChange={(e) => {
                                            descriptionField.onChange(e)
                                            setCount({ ...count, description: countUnicodeCharacters(e.target.value) })
                                        }}
                                    />
                                </Tooltip>
                                <span style={{ color: count.description > 5000 ? "#ed837f" : "#3E3E3E", fontSize: 14, textAlign: 'right', display: 'block' }}>{count.description}/5000</span>

                                <FormErrorMessage style={{ marginTop: "-18px" }}>
                                    <>{errors.description && errors.description.message}</>
                                </FormErrorMessage>
                            </FormControl>

                            <Box mb={5}>
                                <Text {...labelStyles}>
                                    Thumbnail
                                    <Text as="span" color="#E53E3E" ml={1}>
                                        *
                                    </Text>
                                </Text>
                                {/* <SimpleGrid columns={[2, 2, 4]} spacing={4} mt={2}> */}
                                <SimpleGrid
                                    columns={[2, 2, 4]}
                                    key={uploadedThumbURL}
                                    spacing={4}
                                    mt={2}
                                    p={2}
                                    border={thumbnailError ? '2px solid' : '2px solid transparent'}
                                    borderColor={thumbnailError ? '#E53E3E' : 'transparent'}
                                    borderRadius="md"
                                    transition="border-color 0.2s"
                                >
                                    {uploadedThumbnail || uploadedThumbURL ? (
                                        <Box
                                            cursor={'pointer'}
                                            width={'100%'}
                                            aspectRatio={16 / 9}
                                            rounded={'md'}
                                            border={selectedThumbnail == 4 ? 'solid' : 'dashed'}
                                            borderWidth={3}
                                            borderColor={selectedThumbnail == 4 ? 'lyk-green' : '#eaeaea'}
                                            display={'flex'}
                                            justifyContent={'center'}
                                            alignItems={'center'}
                                            onClick={() => {
                                                setThumbnail(4)
                                            }}
                                            pos="relative"
                                            bg="white"
                                        >
                                            {
                                                <Box
                                                    pos="absolute"
                                                    bg="lyk-red"
                                                    rounded="100%"
                                                    p={2}
                                                    opacity={0.7}
                                                    _hover={{
                                                        opacity: 1,
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        setThumbnail(-1)
                                                        setUploadedThumbnail('')
                                                        setUploadedThumbnailFile(null)
                                                        setUploadedThumbURL('')
                                                    }}
                                                >
                                                    <X size={20} color={'white'} />
                                                </Box>
                                            }
                                            <Image
                                                src={uploadedThumbnail || uploadedThumbURL}
                                                width={'100%'}
                                                aspectRatio={16 / 9}
                                                alt="Thumbnail"
                                                objectFit={'cover'}
                                            />
                                        </Box>
                                    ) : (
                                        <FileUploader
                                            handleChange={async (selectedFile: File) => {
                                                const fileSize = selectedFile.size / 1024 / 1024
                                                if (fileSize > 2) {
                                                    toast.error('File size should not exceed 2MB')
                                                    return
                                                }
                                                const isValidResolution = await validateImageResolution(
                                                    selectedFile,
                                                    1280,
                                                    720
                                                )
                                                if (!isValidResolution) {
                                                    toast.error('Minimum thumbnail size: 1280 × 720 pixels.')
                                                    return
                                                }
                                                const base = await getBase64(selectedFile)
                                                setUploadedThumbnail(base)
                                                setUploadedThumbnailFile(selectedFile)
                                                setUploadedThumbURL('')
                                                setThumbnail(4)
                                            }}
                                            name="file"
                                            types={fileTypes}
                                            classes="dragdrop"
                                        >
                                            <Box
                                                cursor={'pointer'}
                                                width={'100%'}
                                                aspectRatio={16 / 9}
                                                rounded={'md'}
                                                border={selectedThumbnail == 4 ? 'solid' : 'dashed'}
                                                borderWidth={3}
                                                borderColor={selectedThumbnail == 4 ? 'lyk-green' : '#eaeaea'}
                                                display={'flex'}
                                                justifyContent={'center'}
                                                alignItems={'center'}
                                                overflow={'hidden'}
                                            >
                                                <Flex alignItems={'center'}>
                                                    <Plus
                                                        size={18}
                                                        color={'#3E3E3E'}
                                                        opacity={0.8}
                                                        className="mr-1"
                                                    />
                                                    <Text fontSize={12} opacity={0.8} color={'#3E3E3E'}>
                                                        Upload thumbnail
                                                    </Text>
                                                </Flex>
                                            </Box>
                                        </FileUploader>
                                    )}


                                </SimpleGrid>
                                {thumbnailError && <Text mt={1} fontSize={'sm'} color={'#ed837f'}>
                                    Thumbnail required.
                                </Text>}
                            </Box>

                            <Box mb={5}>
                                <Stack direction="row" justifyContent="space-between" spacing={4}>
                                    <FormLabel {...labelStyles} mb={0}>
                                        Was this video previously published on another platform?
                                    </FormLabel>

                                    <Switch
                                        isChecked={previouslyPublished}
                                        onChange={(e) => {
                                            setPreviouslyPublished(e.target.checked)
                                            if (!e.target.checked) {
                                                resetField("original_publish_date", { defaultValue: '' })
                                            }
                                        }}
                                    />
                                </Stack>
                            </Box>

                            {previouslyPublished && (
                                <FormControl
                                    mb={5}
                                    isRequired
                                    isInvalid={!!errors.original_publish_date}
                                >
                                    <FormLabel {...labelStyles}>
                                        Publication Date
                                    </FormLabel>

                                    <Input
                                        type="date"
                                        max={format(new Date(), "yyyy-MM-dd")}
                                        {...inputStyles}
                                        {...register("original_publish_date", {
                                            required: "Publication date is required",
                                        })}
                                    />

                                    <Stack direction="row" alignItems={'center'} spacing={2} mt={1}>
                                        <Info color="gray" size='12px'/> 
                                        <Text fontSize="14px" opacity={0.7}>
                                            Select the date when this video was first published on another platform.
                                        </Text>
                                    </Stack>

                                    <FormErrorMessage>
                                        {errors.original_publish_date?.message}
                                    </FormErrorMessage>
                                </FormControl>
                            )}
                            
                            <Box mb={5}>
                                <ControlledSelect
                                    key={videoDetails?.categoryIds}
                                    control={control as unknown as Control<FieldValues>}
                                    isMulti={false}
                                    isClearable={true}
                                    name="category"
                                    id="category"
                                    rules={{
                                        required: 'Category is required',
                                    }}
                                    placeholder="Choose category"
                                    label="Category"
                                    isRequired={true}

                                    options={
                                        categories
                                            ? categories.map((c) => {
                                                return {
                                                    label: c.categoryName,
                                                    value: c.categoryId,
                                                }
                                            })
                                            : []
                                    }
                                />
                            </Box>

                            <Box mb={5}>
                                <ControlledSelect
                                    key={videoDetails?.languageIds}
                                    control={control as unknown as Control<FieldValues>}
                                    isMulti={false}
                                    isClearable={true}
                                    name="language"
                                    id="language"
                                    placeholder="Choose language"
                                    label="Language"
                                    isRequired={true}
                                    rules={{
                                        required: 'Language is required',
                                    }}
                                    options={
                                        languages
                                            ? languages.map((language) => {
                                                return {
                                                    label: language.language,
                                                    value: language.id,
                                                }
                                            })
                                            : []
                                    }
                                />
                            </Box>



                            {/* SECTION 2: AUDIENCE */}
                            <Box mb={5}>
                                <FormControl isRequired={true}>
                                    <FormLabel {...labelStyles} >
                                        Audience, Is it made for kids
                                    </FormLabel>
                                    <RadioGroup value={watch('isMadeForKids')}>
                                        <Stack gap={3} direction="column">
                                            <Radio
                                                {...register('isMadeForKids', { required: 'This field is required' })}
                                                value={'1'}
                                                size={['sm', 'sm', 'md']}
                                                colorScheme='teal'
                                            >
                                                <Text fontSize="sm">Yes, it’s made for kids</Text>
                                            </Radio>
                                            <Radio
                                                {...register('isMadeForKids', { required: 'This field is required' })}
                                                value={'0'}
                                                size={['sm', 'sm', 'md']}
                                                colorScheme='teal'
                                            >
                                                <Text fontSize="sm">No, it’s not made for kids</Text>
                                            </Radio>
                                        </Stack>
                                    </RadioGroup>
                                    {errors.isMadeForKids && <Text color="red.500">{errors.isMadeForKids.message}</Text>}
                                </FormControl>

                                <Text fontSize="sm" color="gray.500" mt={2}>
                                    This helps us show your content to the right audience and comply with platform policies.
                                </Text>
                            </Box>
                            <Box mb={5}>
                                <FormControl>
                                    <Checkbox
                                        isChecked={watch('isRestrictAge')}
                                        {...register('isRestrictAge')}
                                        colorScheme="green"
                                        size={{ base: 'sm', md: 'md' }}
                                    >
                                        Restrict for users under the age of 18
                                    </Checkbox>
                                </FormControl>
                            </Box>
                        </ModalBody>

                        {/* STICKY FOOTER */}
                        <ModalFooter borderTop="1px solid #eee" position="sticky" bottom={0} bg="white">
                            <Button mr={3} onClick={onClose} variant="ghost">
                                Cancel
                            </Button>
                            <Button
                                bg={'lyk-green'}
                                color={'white'}
                                type="submit"
                                isLoading={updateVideo.isPending}
                            >
                                Save Changes
                            </Button>
                        </ModalFooter>
                    </form>
                </FormProvider>
            </ModalContent>
        </Modal>
    )
}


